/**
 * 앱 전역 설정 저장소 (Phase 1 — localStorage)
 *
 * 배경: zoneZoom / editorOptions / tabKindFilter 는 메모리 state 라 새로고침하면
 * 매번 초기화됐다. 여기서 단일 키(`livesql_settings`)에 모아 영속화한다.
 *
 * 설계 메모
 *  - 저장 구조는 App.js 의 state 모양을 **그대로** 따른다. 변환 계층을 두지 않아야
 *    App.js 변경이 최소화되고, 나중에 QBase 테넌트 테이블로 옮길 때도 그대로 직렬화된다.
 *  - theme(`livesql_theme`) 는 개별 키로 남는다. pinnedActions 는 `favorites` 섹션으로
 *    옮겼다(옛 키는 옛 빌드 호환을 위해 함께 쓴다 — utils/actionFavorites.js).
 *  - 부분 저장/버전 확장을 위해 항상 기본값과 **깊은 병합** 후 반환한다.
 */

const STORAGE_KEY = 'livesql_settings';
const SETTINGS_VERSION = 1;

/** 기본값 — App.js 의 useState 초기값과 일치해야 한다. */
export const DEFAULT_SETTINGS = {
  version: SETTINGS_VERSION,
  // SQL 에디터 옵션 (compactGrid 는 결과 그리드용이지만 기존 state 구조를 유지)
  editorOptions: {
    autocomplete: true,
    showComments: true,
    columnSelection: false,
    compactGrid: false,
    // 실행계획 탭의 오른쪽 「오브젝트 상세」 — 늘 펼쳐져 있어 계획 본문이 좁다는 지적을 받아
    // **기본은 꺼짐**이고 결과 툴바의 토글로 켠다(사용자 요청 2026-09-20).
    planObjectDetail: false,
    // 결과 표의 「사용자 주석」 — 영역명과 같은 컬럼 오른쪽에 그 값의 주석 열을 붙인다(기본 꺼짐 — 켜면 주석 조회 쿼리가 돈다, 2026-10-29)
    userComments: false,
    // 편집기 탭 줄 수 — 1 = 한 줄(가로 스크롤), 2·3 = 여러 줄로 바꿈(탭 우클릭 메뉴, 2026-10-26)
    tabRows: 1,
  },
  // 영역별 폰트 크기(px) — Ctrl+휠 줌과 설정 화면이 같은 값을 공유
  zoneZoom: {
    explorer: 12,
    editor: 13,
    result: 12,
    plan: 12,
    tableInfo: 12,
  },
  // 통합 탭 헤더의 종류별 표시 필터
  tabKindFilter: {
    result: true,
    plan: true,
    tableInfo: true,
  },
  // 결과 그리드 표시 포맷 (Phase 2)
  gridFormat: {
    nullText: '[NULL]',        // NULL 을 무엇으로 보여줄지 (빈 문자열이면 공백)
    dateFormat: 'iso',         // 'iso' | 'date' | 'locale' | 'raw'
    numberGrouping: true,      // 천 단위 구분 기호
  },
  // SQL 포매팅 (Phase 3) — sql-formatter v15 옵션에 대응
  sqlFormat: {
    language: 'plsql',                 // 방언 (sql-formatter language)
    keywordCase: 'upper',              // 'upper' | 'lower' | 'preserve'
    tabWidth: 4,                       // 들여쓰기 칸 수
    linesBetweenQueries: 2,            // 문장 사이 빈 줄
    logicalOperatorNewline: 'before',  // AND/OR 를 줄 앞/뒤 어디에 둘지
    expressionWidth: 50,               // 한 줄 최대 폭(넘으면 줄바꿈)
    denseOperators: false,             // 연산자 주변 공백 제거
    newlineBeforeSemicolon: false,     // 세미콜론을 새 줄에
  },
  // SQL 템플릿(스니펫) — 실제 기본 목록은 utils/sqlSnippets.js 가 가진다.
  // null 이면 '사용자가 손댄 적 없음' → loadSnippets() 가 기본값을 돌려준다.
  snippets: null,
  // SQL 에이전트 (modules/assistant). 토큰 같은 비밀은 여기에 두지 않는다 —
  // localStorage 는 XSS 로 읽히고 사용자별로 나뉘지도 않는다. 자격증명은 서버가 암호화 보관한다.
  assistant: {
    provider: 'claude',
    model: 'default',
    egressComments: true,     // 카탈로그 주석을 LLM 에 보낼지
    egressSampleRows: false,  // 샘플 행 조회 허용 여부 (기본 끔 — 고객 DB 반출 정책)
    askTimeoutMin: 10,        // 되묻기 대기 시간(분)
    // 사용자의 스킬 수준 — 답의 깊이·용어·튜닝 보고서 형식이 갈린다.
    // 값은 백엔드 skillLevel.js 와 같아야 한다: novice | developer | expert
    skillLevel: 'developer',
    // **SQL 튜닝 수준**(1~6) — 스킬 수준과 다른 축이다. 스킬 수준은 답의 형태를 바꾸고,
    // 이것은 에이전트가 **어디까지 해도 되는지**(실측·대안 실행·임시 인덱스)의 상한이다.
    // 값은 백엔드 tuning/tuningLevel.js 와 같아야 한다.
    // 기본 L4(2026-10-29 사용자 결정) — 대안 실행(L5)·임시 인덱스(L6)는 사용자가 올려서 쓴다
    tuningLevel: 4,
  },
  // 메뉴 즐겨찾기 — 읽기·쓰기는 utils/actionFavorites.js 한 곳에서.
  //   actionGroup: 편집기 툴바에 붙인 액션 id 순서('separator' 포함). null = 손댄 적 없음
  //   hiddenFixed: 원래 화면에 있는 버튼 중 사용자가 끈 것
  favorites: {
    actionGroup: null,
    hiddenFixed: [],
  },
  // 화면 확대/축소(%) — 앱 전체 한 배율. 읽기·쓰기·적용은 utils/appZoom.js 한 곳
  uiZoom: 100,
  // 마우스 커서 크기 — 툴팁을 커서 아래 이만큼 띄워 커서에 가려지지 않게 한다(2026-10-23 신고: 큰 커서에 툴팁 글이 가려졌다).
  // 앱은 OS 의 커서 크기를 알 수 없어 사람이 고른다. 값 → 간격은 CURSOR_GAP_PX
  pointer: {
    cursorSize: 'normal',
  },
  // (옛 `layout.explorerAutoCollapse` — 탐색기 자동 접기를 없애(2026-10-19) 걷어 냈다(2026-10-29). 저장본에 남은 값은 mergeWithDefaults 가 그대로 둔다)
  // 실행 안전장치 (Phase 2)
  execution: {
    confirmOnProd: true,       // 운영 연결에서 DML/DDL 실행 전 확인
    warnMissingWhere: true,    // WHERE 없는 UPDATE/DELETE 경고
    // 실행계획을 담을 표(EXPLAIN PLAN INTO). 비우면 SYS.PLAN_TABLE$ — 스키마에 옛 PLAN_TABLE 이 있으면
    // 그것이 먼저 잡혀 이름이 30자에서 잘리고 「old version」 노트가 붙는다(backend database/planTable.js)
    planTable: 'SYS.PLAN_TABLE$',
    // 예상 실행계획의 표시 형식(DBMS_XPLAN.DISPLAY 의 format) — 기능마다 따로 둔다(2026-10-13).
    //   편집기 Ctrl+E: 예전에는 늘 ALL 이라 잘 보지 않는 Column Projection Information 까지 나왔다 → 기본을 TYPICAL 로
    planFormat: 'TYPICAL',
    // 편집기 실행계획(Ctrl+E)의 종류·실측 옵션(2026-10-26 — SQL 실행계획 분석을 편집기 실행계획에 합쳤다). 실행계획 탭의 「옵션」 이 고친다
    //   종류 — estimated = 예상(EXPLAIN PLAN), actual = 실제(실행 후 줄별 실측)
    planMode: 'estimated',
    planActualFormat: 'ALLSTATS LAST',  // 실제일 때 DBMS_XPLAN.DISPLAY_CURSOR 형식
    //   실측 수집 — session = STATISTICS_LEVEL=ALL, hint = GATHER_PLAN_STATISTICS, none = 더하지 않음
    planStatsMethod: 'session',
    planMonitorReport: false,           // SQL Monitor 리포트도 받기(Tuning Pack) — 기본 꺼짐
  },
};

// [i18n] 모듈 스코프라 t() 를 못 쓴다. 아래 상수들은 **문구 대신 키**를 담고,
// 화면에 그릴 때 컴포넌트에서 t(labelKey) 로 번역한다.
// value 는 저장되는 설정값이라 절대 번역하지 않는다. label 이 그대로 남은 항목은
// 'YYYY-MM-DD' 나 'SELECT' 처럼 번역 대상이 아닌 리터럴이다.

/**
 * 예상 실행계획의 표시 형식 — value 는 DBMS_XPLAN.DISPLAY 의 format 그대로다.
 * **백엔드 queryExecutor.ALLOWED_FORMAT_OPTIONS 에 있는 값이어야 한다**(없는 값은 서버가 조용히 ALL 로 돌린다).
 */
export const PLAN_FORMATS = [
  { value: 'BASIC', label: 'BASIC', hintKey: 'settings:exec.fmt.basic' },
  { value: 'TYPICAL', label: 'TYPICAL', hintKey: 'settings:exec.fmt.typical' },
  { value: 'ALL -PROJECTION', label: 'ALL -PROJECTION', hintKey: 'settings:exec.fmt.allNoProjection' },
  { value: 'ALL', label: 'ALL', hintKey: 'settings:exec.fmt.all' },
  { value: 'ADVANCED', label: 'ADVANCED', hintKey: 'settings:exec.fmt.advanced' },
];

/** 실제 실행계획(DISPLAY_CURSOR) 형식 — 서버 ALLOWED_FORMAT_OPTIONS 에 있는 것만 */
export const ACTUAL_PLAN_FORMATS = ['ALLSTATS LAST', 'ALLSTATS LAST +PEEKED_BINDS', 'ALLSTATS LAST +OUTLINE', 'ALL +STATS', 'TYPICAL', 'ALL -PROJECTION', 'ALL'];
export const PLAN_STATS_METHODS = ['session', 'hint', 'none'];

/** 날짜 표시 방식 선택지 — 설정 화면 라벨과 실제 포맷터가 같은 목록을 본다. */
export const DATE_FORMATS = [
  { value: 'iso',    label: 'YYYY-MM-DD HH:MM:SS', hintKey: 'settings:dateFormat.isoHint' },
  { value: 'date',   label: 'YYYY-MM-DD',          hintKey: 'settings:dateFormat.dateHint' },
  { value: 'locale', labelKey: 'settings:dateFormat.locale', hintKey: 'settings:dateFormat.localeHint' },
  { value: 'raw',    labelKey: 'settings:dateFormat.raw',    hintKey: 'settings:dateFormat.rawHint' },
];

/**
 * 1단계 깊이까지 병합 — 값이 객체면 키 단위로, 아니면 그대로 덮어쓴다.
 *
 * **모르는 키를 버리지 않는다.** 예전에는 아는 키만 골라 새 객체를 만들었는데(그때 의도는
 * "옛 버전의 사라진 키가 살아 돌아오지 않게"), 그것 때문에 **새 빌드가 저장한 설정이 예전 빌드를
 * 한 번 거치면 영구히 사라졌다.** saveSection 이 이미 가지치기된 결과를 읽어 전체를 되쓰기 때문에,
 * 예전 빌드로 설정 하나만 바꿔도 새 섹션이 디스크에서 지워진다. 스탠드얼론은 현장마다 빌드가
 * 갈리고 %APPDATA%/LiveSQL 을 공유하므로 실제로 일어난다.
 *
 * 죽은 키가 남는 비용은 없다시피 하고, 잃은 설정은 되돌릴 수 없다 — openflow
 * docs/SCHEMA_COMPAT.md 의 normalizeSettings 와 같은 결론이다.
 */
function mergeWithDefaults(saved) {
  const defaults = JSON.parse(JSON.stringify(DEFAULT_SETTINGS));
  if (!saved || typeof saved !== 'object') return defaults;

  // 저장본을 바탕에 깔고 기본값으로 보충한다 — 이 빌드가 모르는 최상위 섹션이 그대로 남는다.
  const out = { ...saved };
  for (const [section, defaultVal] of Object.entries(defaults)) {
    const savedVal = saved[section];
    if (savedVal === undefined || savedVal === null) { out[section] = defaultVal; continue; }
    const bothPlainObjects = defaultVal && typeof defaultVal === 'object' && !Array.isArray(defaultVal)
      && typeof savedVal === 'object' && !Array.isArray(savedVal);
    // 섹션 안에서도 저장본이 뒤 — 아는 키는 저장값이 이기고, 모르는 키는 살아남는다
    out[section] = bothPlainObjects ? { ...defaultVal, ...savedVal } : savedVal;
  }

  // 버전은 **끌어내리지 않는다.** 새 빌드가 찍은 값을 예전 빌드가 1 로 되돌리면, 다시 새 빌드로
  // 돌아왔을 때 형식 판정이 틀어진다. 키를 보존하므로 왕복 자체는 무손실이다.
  const savedVersion = Number(saved.version) || 0;
  if (savedVersion > SETTINGS_VERSION) {
    console.warn(`[appSettings] 저장된 설정이 이 빌드보다 새 형식입니다 (설정 v${savedVersion} > 프로그램 v${SETTINGS_VERSION}). `
      + '값은 그대로 보존하지만, 최신 버전에서 확인하세요.');
  }
  out.version = Math.max(SETTINGS_VERSION, savedVersion);
  return out;
}

/** 저장된 전체 설정 반환 (없거나 깨졌으면 기본값). 예외를 던지지 않는다. */
export function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return mergeWithDefaults(null);
    return mergeWithDefaults(JSON.parse(raw));
  } catch (_) {
    return mergeWithDefaults(null);
  }
}

/** 특정 섹션만 반환 — App.js 의 useState 초기화용. */
export function loadSection(section) {
  const all = loadSettings();
  return all[section] !== undefined ? all[section] : DEFAULT_SETTINGS[section];
}

/** 섹션 하나를 저장 (다른 섹션은 보존). 저장 실패는 무시한다(사생활 모드 등). */
export function saveSection(section, value) {
  try {
    const all = loadSettings();
    all[section] = value;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (_) { /* 저장 불가 환경에서도 앱은 동작해야 한다 */ }
}

/** 전체 초기화 — 설정 화면의 '기본값 복원'. */
export function resetSettings() {
  try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
  return mergeWithDefaults(null);
}

/** 폰트 크기 허용 범위 — Ctrl+휠 줌(App.js)과 설정 화면이 같은 한계를 쓴다. */
export const ZOOM_MIN = 8;
export const ZOOM_MAX = 40;

/** 영역 키 → 사람이 읽는 이름 (설정 화면 라벨) */
export const ZONE_LABEL_KEYS = {
  explorer: 'settings:zone.explorer',
  editor: 'settings:zone.editor',
  result: 'settings:zone.result',
  plan: 'settings:zone.plan',
  tableInfo: 'settings:zone.tableInfo',
};

/** SQL 포매팅 선택지 — 설정 화면과 포맷터가 같은 목록을 본다. */
/** 마우스 커서 크기 → 툴팁을 커서 끝(hotspot) 아래로 띄우는 간격(화면 px). Windows 기본 커서는 32px 안팎이다 */
export const CURSOR_SIZES = [
  { value: 'normal', labelKey: 'settings:general.cursor.normal' },
  { value: 'large', labelKey: 'settings:general.cursor.large' },
  { value: 'xlarge', labelKey: 'settings:general.cursor.xlarge' },
];
export const CURSOR_GAP_PX = { normal: 34, large: 56, xlarge: 88 };
export function cursorGapPx() {
  const v = (loadSection('pointer') || {}).cursorSize;
  return CURSOR_GAP_PX[v] || CURSOR_GAP_PX.normal;
}

export const SQL_LANGUAGES = [
  { value: 'plsql',      label: 'Oracle / PL-SQL' },
  { value: 'postgresql', label: 'PostgreSQL' },
  { value: 'mysql',      label: 'MySQL' },
  { value: 'sqlite',     label: 'SQLite' },
  { value: 'transactsql',label: 'SQL Server (T-SQL)' },
  { value: 'sql',        labelKey: 'settings:sqlLang.standard' },
];

export const KEYWORD_CASES = [
  { value: 'upper',    labelKey: 'settings:keywordCase.upper',    hint: 'SELECT' },
  { value: 'lower',    labelKey: 'settings:keywordCase.lower',    hint: 'select' },
  { value: 'preserve', labelKey: 'settings:keywordCase.preserve' },
];

export const OPERATOR_NEWLINES = [
  { value: 'before', labelKey: 'settings:opNewline.before', hint: 'AND x = 1' },
  { value: 'after',  labelKey: 'settings:opNewline.after',  hint: 'x = 1 AND' },
];
